<template>
	<div>
		<b-modal 
			@hidden="hideModal" 
			ref="addFilesModal" 
			id="addFilesModal" 
			size="lg"
			:title="modalTitle"
            :ok-title="$t('misc.ok')" 
			:cancel-title="$t('misc.cancel')"
			@ok="uploadDocuments"
		>
			<div class="row mt-2" v-if="getData.getFiles.length > 0 && hasFilesReadPermission">
                <div class="col-lg-12">
                    <label>{{ $t('table.uploaded_files') }}</label>
                </div>
                <div class="col-lg-6" v-for="file in getData.getFiles" :key="file.id">
                    <div class="card p-2">
                        <div style="width: 100%; display: block">
                            <p 
                                class="float-left customLink" 
                                @click="onDownloadFile(file.id)" 
                                :title="file.file_name"
                            >
                                {{file.file_name}}
                            </p>
                            <p 
                                v-if="hasFilesDeletePermission"
                                class="float-right p-0 m-0">
                                <i class='fa fa-2x fa-trash mt-2 text-danger sortable cursor-pointer' @click="onDeleteFile(file.id)"></i>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div 
                v-if="hasFilesCreatePermission && canUploadFiles"
                class="row mt-2"
            >
                <div class="col-lg-12">
                    <div class="row mb-3">
                        <div class="col-lg-12">
                            <label v-if="row && getData.getFiles.length > 0">{{ $t('table.upload_more_files') }}</label>
                            <label v-else>{{ $t('table.upload_files') }}</label>
                        </div>
                        <div class="col-lg-12">
                            <vue-dropzone class="dropzone" ref="document" id="dropzone" :options="dropzoneOptions"></vue-dropzone>
                        </div>
                    </div>
                </div>
            </div>
		</b-modal>

		<confirm-dialog ref="deleteFileModal" :title="actionTitleDeleteFile" @buttonClick="deleteFileAction">
            {{ $t('messages.delete_file') }}
        </confirm-dialog>
	</div>
</template>

<script>
import Vue from 'vue';
import http from '@/libs/http';
import ConfirmDialog from '@/components/global/modals/ConfirmDialog.vue';
import * as notificationIcons from '@/components/global/miscellaneous/notificationIcons.js';
import miniToastr from 'mini-toastr';
import vue2Dropzone from 'vue2-dropzone';
import '@/assets/css/vue2Dropzone.css';

export default {
	name: 'AddFilesModal',
	components: {
		ConfirmDialog,
		notificationIcons,
		vueDropzone: vue2Dropzone,
	},
	props: {
		files: {
			type: Array,
			default: function () {
				return [];
			},
		},
        fileType: {
            type: String,
            required: true,
        },
        filterBy: {
            type: String,
            required: true,
        },
        canUploadFiles: {
            type: Boolean,
            default: true,
        },
	},
	data() {
		return {
			actionTitleDeleteFile: Vue.i18n.translate('messages.delete_file_title'),
			modalTitle: Vue.i18n.translate('table.add_files'),
			row: null,

			file_id: null,
            hasFilesReadPermission: false,
            hasFilesCreatePermission: false,
            hasFilesDeletePermission: false,

			dropzoneOptions: {
                url: '/',
                thumbnailWidth: 150,
                addRemoveLinks: true,
                autoProcessQueue: false,
                uploadMultiple: true,
                dictDefaultMessage: '<i class="fa fa-cloud-upload"></i>' + ' ' + Vue.i18n.translate('table.upload_files'),
                maxFiles: 3
            },
            getData: {
                getFiles: [],
            },
		}
	},
	mounted() { 
	},
	methods: {
		successMsg(msg) {
			miniToastr.success(msg, Vue.i18n.translate('messages.success'));
		},
		errorMsg(msg) {
			miniToastr.error(msg, Vue.i18n.translate('messages.error'));
		},
		showModal(row) {
            this.hasFilesReadPermission = this.$store.getters.hasPermission(['files'], ['read']);

            if (!this.hasFilesReadPermission) {
                this.errorMsg(Vue.i18n.translate('errors.permission.read'));
                return 0;
            }

            this.hasFilesCreatePermission = this.$store.getters.hasPermission(['files'], ['create']);
            this.hasFilesDeletePermission = this.$store.getters.hasPermission(['files'], ['delete']);

			this.row = row;
            this.onViewFiles(row.id);
			this.$refs.addFilesModal.show();
		},
        onViewFiles (id) {
            this.getData.getFiles.length = 0;
            let data = http.route('file?get_all=1&filters[' + this.filterBy + ']=' + id).get();
            data.then(response => {
                if(response.data == false) {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                } else {
                    this.getData.getFiles = response.data;
                }
            });
        },
		hideModal() {
            this.getData.getFiles = [];
		},
		uploadDocuments (e) {
            if (this.hasFilesCreatePermission && typeof this.$refs.document != 'undefined') { 
                e.preventDefault();
                
                var file = this.$refs.document.getQueuedFiles();
                for (let order = 0; order < file.length; order++) {
                    var formData = new FormData();
                    formData.append('file', file[order]);
                    formData.append('type', this.fileType);
                    formData.append(this.filterBy, this.row.id);
                    formData.append('project_id', this.$route.params.id);
                    let data = http.route('file').post(
                        formData,
                            {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    ).then(response => {
                        if(response.data == false) {
                            Object.keys(response.errorMessage).forEach(key => {
                                this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                            });
                        } else {
                            this.$refs.addFilesModal.hide();
                            this.successMsg(Vue.i18n.translate('messages.file_uploaded'));
                        }
                    });
                }
            }
        },
		onDownloadFile (id) {
            let data = http.route('file/' + id).get(
            {
                export_pdf: 1
            }, true);
            data.then(response => {
                if (!response.isError) {
                    this.successMsg(Vue.i18n.translate('messages.file.file_download'));
                    let data = http.route('get_content/file/'+response.data.id+'/'+response.data.file_name).downloadPdf({}, response.data.file_name).then(res => {});
                } else {
                    this.errorMsg(Vue.i18n.translate('messages.file.error_download'));
                }
            });
        },
		onDeleteFile (id) {
            this.file_id = id;
            this.$refs.deleteFileModal.show();
        },
        deleteFileAction (button) {
            if (button == 1) {
				let data = http.route('file/' + this.file_id).delete();
				data.then(response => {
					if (!response.isError) {
						this.successMsg(Vue.i18n.translate('messages.deleted_file_message'));
						this.file_id = null;
						this.$refs.addFilesModal.hide();
					} else {
						Object.keys(response.errorMessage).forEach(key => {
                            this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                        });
					}
				});
            }

            this.$refs.deleteFileModal.hide();
        },
	}
}
</script>


// WEBPACK FOOTER //
// src/components/global/modals/AddFilesModal.vue